import {assert} from 'Asserts';
import {DataFrame, DataFrameColumnDef, DataFrameRow} from 'data-frames/DataFrameTypes';
import {stringListAccessor} from 'data-frames/util/AccessorUtils';
import {DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {JSONValue} from 'util/Types';

import {DEFAULT_COLUMN_WIDTH} from '../DataFrameTableEditableConstants';
import {
  DataFrameToTableColumnInfoInitializer,
  BaseColumnConstraint,
  BaseStructuredTableColumn,
} from '../DataFrameTableEditableTypes';

export default function jsonListColumnInfoInitializer(
  {systemName, type: {isNullable}}: DataFrameColumnDef,
  dataFrame: DataFrame,
  baseColumnConstraint: BaseColumnConstraint,
  baseStructuredTableColumn: BaseStructuredTableColumn,
  baseAccessor: DataFrameValueAccessor
): ReturnType<DataFrameToTableColumnInfoInitializer> {
  const rawValueAccessor = stringListAccessor(baseAccessor);

  const width =
    dataFrame.rows.reduce(
      (max: number, row: DataFrameRow) => Math.max(max, rawValueAccessor(row).length),
      0
    ) * DEFAULT_COLUMN_WIDTH;

  return {
    columnConstraint: {
      ...baseColumnConstraint,
      columnDefinition: {
        type: TableColumnType.ROW_DELEGATE,
        isRequired: !isNullable,
      },
      structuredTableColumn: {
        ...makeStructuredTableColumn({
          ...baseStructuredTableColumn,
          columnType: TableColumnType.ROW_DELEGATE,
        }),
        ...baseStructuredTableColumn,
      },
    },
    delegatedColumnDefinitionAccessor: (row: DataFrameRow): ColumnDefinition => ({
      type: TableColumnType.OPTION_SEARCH_SELECTOR,
      options: rawValueAccessor(row),
      isMulti: true,
      isRequired: !isNullable,
    }),
    dataFrameValueToTableCell: (row: DataFrameRow): Cell => ({
      text: '',
      typedValue: {
        type: BaseTypedValueType.ARRAY,
        rawValue: rawValueAccessor(row).map((value) => ({
          type: BaseTypedValueType.STRING,
          rawValue: value,
        })),
      },
    }),
    tableCellToDataFrameValue: ({text, typedValue}: Cell, originalRow: DataFrameRow): JSONValue => {
      const textOptions = rawValueAccessor(originalRow);
      const valueOptions = baseAccessor(originalRow);

      assert(Array.isArray(valueOptions), 'Value options should be an array');

      if (typedValue != null && typedValue.type === BaseTypedValueType.ARRAY) {
        return typedValue.rawValue.map((typedValue) => {
          assert(typedValue.type === BaseTypedValueType.STRING, 'Value should be a string');
          let value;
          for (const [textOptionIndex, textOption] of textOptions.entries()) {
            if (textOption === typedValue.rawValue) {
              value = valueOptions[textOptionIndex];
              break;
            }
          }

          assert(value != null, `Typed value not found in text options`, {
            text,
            typedValue,
            textOptions,
            valueOptions,
            originalRow,
          });

          return value;
        });
      }

      if (text === '') {
        return [];
      }

      const textValues = text.split(', ');

      return textValues.map((textValue) => {
        let value;
        for (const [textOptionIndex, textOption] of textOptions.entries()) {
          if (textOption === textValue) {
            value = valueOptions[textOptionIndex];
            break;
          }
        }

        assert(value != null, `Text value not found in text options`, {
          text,
          typedValue,
          textOptions,
          valueOptions,
          originalRow,
        });

        return value;
      });
    },
    width,
  };
}
